import * as React from 'react'
import { BsFillDiamondFill, FaMapMarker } from 'react-icons/all'
import Loader from 'react-loader-spinner'
import Swal from 'sweetalert2'

import styled from '@theme/styled-components'
import Theme from '@theme/Theme'

import ScreenContainer from '@components/screenContainer/screenContainer'
import MonitorFilters from '@components/filter/monitorFilters'
import BusinessLegend from '@components/legend/BusinessLegend'
import PaginationComponent from '@components/pagination/PaginationComponent'
import Button from '@components/button/button'

import useI18n from '@store/i18n/useI18n'

import apis from '@api/api'

import downloadCsv from '@utils/CSVutils'

interface Props {
  navigation: Navigation
}

const ICON_SIZE = 30
const TABLE_ORDER = (i18n: I18n) => [
  `${i18n.t('components.monitorByRegion.tableHeader.pictogram')}`,
  `${i18n.t('components.monitorByRegion.tableHeader.businessId')}`,
  `${i18n.t('components.monitorByRegion.tableHeader.responsible')}`,
  `${i18n.t('components.monitorByRegion.tableHeader.company')}`,
  `${i18n.t('components.monitorByRegion.tableHeader.address')}`,
  `${i18n.t('components.monitorByRegion.tableHeader.number')}`,
  `${i18n.t('components.monitorByRegion.tableHeader.date')}`,
  `${i18n.t('components.monitorByRegion.tableHeader.buttonRedirection')}`,
]

const MonitorByRegion = ({ navigation }: Props) => {
  const i18n = useI18n()

  const [screenStatus, setScreenStatus] = React.useState<ScreenStatus>('loading')
  const [legendOpened, setLegenOpened] = React.useState(false)

  const [businessesBoard, setBusinessesBoard] = React.useState<AdminBusinessesExtended>()
  const [currentPage, setCurrentPage] = React.useState(1)

  const [exportLoading, setExportLoading] = React.useState(false)
  const [exportQuery, setExportQuery] = React.useState<string>()

  const businessBoardTable: string[][] = React.useMemo(() => {
    const tableToDisplay = TABLE_ORDER(i18n).map((header) => [header])
    if (!!businessesBoard) {
      businessesBoard.businesses.map((business) => {
        //Pictogramme de l'affaire déterminé par sa source et son état dans la première colonne
        tableToDisplay[0].push(`${business.state},${business.type}`)
        //Numéro d'affaires dans la seconde colonne
        tableToDisplay[1].push(business.idGrdf)
        //CA de l'affaire
        tableToDisplay[2].push(business.userName)
        //Entreprise de l'affaire
        tableToDisplay[3].push(business.companies)
        //Adresse de l'affaire
        tableToDisplay[4].push(business.address)
        //Nombre de déclarations sur l'affaire
        tableToDisplay[5].push(`${business.declarationsCount}`)
        //Date de la dernière déclaration liée à l'affaire
        tableToDisplay[6].push(
          !!business.lastDeclaration ? i18n.t('common.date', { date: new Date(business.lastDeclaration) }) : '/'
        )
        //ID de l'affaire pour la redirection
        tableToDisplay[7].push(business.id)
      })
    }
    return tableToDisplay
  }, [businessesBoard])

  const displayValue = (row: string, rowIdx: number, columnIdx: number) => {
    if (rowIdx === 0) {
      return (
        <TabValue header odd>
          {row}
        </TabValue>
      )
    } else {
      switch (columnIdx) {
        case 0:
          //Affichage du pictogramme de l'affaire
          const [state, source] = row.split(',')
          if (source === 'SIROCCO') {
            return (
              <FaMapMarker
                size={ICON_SIZE}
                color={state === 'inprogress' ? Theme.colors.markerInProgress : Theme.colors.markerStopped}
              />
            )
          } else {
            return (
              <BsFillDiamondFill
                size={ICON_SIZE}
                color={state === 'inprogress' ? Theme.colors.markerInProgress : Theme.colors.markerStopped}
              />
            )
          }

        case 7:
          //Affichage du bouton de redirection
          return (
            <GoToBusiness onClick={() => navigation.push(`/business/${row}`)}>
              {i18n.t('components.monitorByRegion.goToBusiness')}
            </GoToBusiness>
          )

        default:
          return <TabValue odd={rowIdx % 2 === 0}>{row}</TabValue>
      }
    }
  }

  const exportBusinesses = () => {
    setExportLoading(true)
    if (!!exportQuery) {
      apis.admin.business.export(exportQuery).then((res) => {
        if (!res) {
          Swal.fire({
            title: i18n.t('components.adminData.noexport.title'),
            text: i18n.t('components.adminData.noexport.text'),
            icon: 'info',
            showCancelButton: false,
            confirmButtonColor: '#3A8641',
            confirmButtonText: i18n.t('components.adminData.noexport.confirm'),
          })
        } else {
          downloadCsv(res, 'export_affaires.csv')
        }
        setExportLoading(false)
      })
    }
  }

  return (
    <ScreenContainer
      menu={
        <LeftContainer>
          <ScreenTitleContainer>
            <ScreenTitle>{i18n.t('components.monitorByRegion.title')}</ScreenTitle>
            <Back onClick={() => navigation.push('/')}>{i18n.t('components.menu.back')}</Back>
          </ScreenTitleContainer>
          <MonitorFilters
            screenStatus={screenStatus}
            setScreenStatus={setScreenStatus}
            setBusinessesBoard={setBusinessesBoard}
            setExportQuery={setExportQuery}
            currentPage={currentPage}
          />
        </LeftContainer>
      }
      content={
        <>
          <RightContainer>
            {screenStatus === 'loading' ? (
              <StatusContainer>
                <Loader type="ThreeDots" color={Theme.colors.mapButtonLoader} height={30} />
              </StatusContainer>
            ) : (
              <>
                {!!businessesBoard && (
                  <>
                    <TitleContainer>
                      <ScreenTitle>
                        {i18n.t('components.monitorByRegion.businessesNumber', { count: businessesBoard.size })}
                      </ScreenTitle>
                      <Button
                        title={i18n.t('components.monitorByRegion.export')}
                        onClick={exportBusinesses}
                        size="small"
                        type="action"
                        status={!!exportQuery && !exportLoading ? 'active' : 'loading'}
                      />
                    </TitleContainer>
                    <BoardContainer>
                      {businessBoardTable.map((column, columnIdx) => (
                        <TabColumn key={columnIdx} index={columnIdx}>
                          {column.map((row, rowIdx) => (
                            <TabRow key={`${columnIdx} - ${rowIdx}`} header={rowIdx === 0} odd={rowIdx % 2 === 0}>
                              {displayValue(row, rowIdx, columnIdx)}
                            </TabRow>
                          ))}
                        </TabColumn>
                      ))}
                    </BoardContainer>
                    <PaginationComponent
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      numberBusinesses={businessesBoard.size}
                    />
                  </>
                )}
              </>
            )}
          </RightContainer>
          <BusinessLegend isOpened={legendOpened} setIsOpened={setLegenOpened} source="admin" />
        </>
      }
    />
  )
}
export default MonitorByRegion

const LeftContainer = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px 10px 0 10px;
  overflow: hidden;
`
const ScreenTitle = styled('div')`
  ${(props) => props.theme.fonts.title};
  margin-right: 20px;
`
const ScreenTitleContainer = styled('div')`
  border: 0px solid ${(props) => props.theme.colors.menuBorder};
  border-bottom-width: 1px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 10px;
  margin: 0 10px 15px 10px;
`
const Back = styled('div')`
  ${(props) => props.theme.fonts.backLibelle};
  cursor: pointer;
`
const RightContainer = styled('div')`
  background-color: ${(props) => props.theme.colors.filterBackgroundColor};
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 70px 50px 50px;
  justify-content: flex-start;
  overflow: scroll;
`
const StatusContainer = styled('div')`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
`
const BoardContainer = styled('div')<{ height?: number }>`
  flex: 1;
  display: flex;
  flex-direction: row;

  margin: 25px;
  border-radius: 10px;
  box-shadow: 0px 0px 5px 1px #2224;
  overflow: scroll;
  ${(props) => props.height && `max-height: ${props.height}px`};

  background: url('/loading.gif') white no-repeat center;
`
const TabColumn = styled('div')<{ index: number }>`
  ${(props) =>
    props.index === 0
      ? `
    width: 50px;
  `
      : `
    display: flex;
    flex: ${props.index === 4 ? '2' : '1'};
  `}
  flex-direction: column;
  background-color: white;
`
const TabRow = styled('div')<{ header: boolean; odd: boolean }>`
  border: 0px solid ${(props) => props.theme.colors.headerBackgroundColor};
  border-right-width: 1px;
  border-bottom-width: 1px;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  height: 50px;
  max-height: 50px;
  min-height: 50px;

  background-color: ${(props) =>
    props.header
      ? props.theme.colors.headerBackgroundColor
      : props.odd
      ? props.theme.colors.oddBackgroundColor
      : props.theme.colors.tabBackgroundColor};
`
const TabValue = styled('div')<{ header?: boolean; odd?: boolean }>`
  ${(props) => (props.header ? props.theme.fonts.sectionTitle : props.theme.fonts.label)};
  ${(props) => (props.header ? `font-size: 15px; line-height: 15px;` : `font-size: 13px; line-height: 13px;`)};

  color: ${(props) =>
    props.header
      ? props.theme.colors.headerColor
      : props.odd
      ? props.theme.colors.oddColor
      : props.theme.colors.tabColor};
  overflow: hidden;
`
const GoToBusiness = styled('nav')`
  text-decoration: underline;
  color: ${(props) => props.theme.colors.primary_blue};

  &: hover {
    cursor: pointer;
  }
`
const TitleContainer = styled('div')`
  display: flex;
`
